import { useUpdateWorkflowTimeAndStateMutation } from '@/__generated__/graphql';
import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from './useRouter';
import * as state from '@/state';
import { useAtom, useAtomValue } from 'jotai';
import { useGetStructureId } from './useGetStructureId';

export function useWorkFlowTimeAndStateUpdater() {
  const router = useRouter();
  const zoneIdFromUrl = router?.query?.zoneid;
  const selectedZone = useAtomValue(state.selectedZone);
  const selectedZoneRef = useRef(selectedZone);
  const zoneId = zoneIdFromUrl ? zoneIdFromUrl.toString() : selectedZone?.id;
  const structureId = useGetStructureId();
  const [startTimeStamp, setStartTimeStamp] = useAtom(state.startTimeStampForTimeLogs);
  const startTimeStampRef = useRef<Date>(startTimeStamp);

  const [mutateWorkflowTimeAndState] = useUpdateWorkflowTimeAndStateMutation({
    refetchQueries: ['zonesForStructureForTagging'],
  });

  const updatedWorkflowTimeAndState = useCallback(
    async ({
      typeOfTimeLog,
      trackedTime,
      trackedTimeStampStart,
      pausedBy,
    }: {
      typeOfTimeLog?: string;
      trackedTime?: boolean;
      trackedTimeStampStart?: Date;
      pausedBy?: string;
    }) => {
      if (!zoneId) {
        return;
      }
      const zone = selectedZoneRef.current;
      await mutateWorkflowTimeAndState({
        variables: {
          input: {
            structureId,
            zoneId,
            assigneeId: zone?.workflowStatus?.assignedToUser?.id,
            timeInMs: new Date().getTime() - (startTimeStampRef.current.getTime() ?? 0),
            typeOfTimeLog,
            statusId: zone?.workflowStatus?.workflowStageId,
            trackedTime: trackedTime,
            trackedTimeInMs: new Date().getTime() - (trackedTimeStampStart?.getTime() ?? 0),
            pausedBy,
          },
        },
      }).finally(() => {
        setStartTimeStamp(new Date());
      });
    },
    [mutateWorkflowTimeAndState, setStartTimeStamp, structureId, zoneId]
  );

  useEffect(() => {
    startTimeStampRef.current = startTimeStamp;
    selectedZoneRef.current = selectedZone;
  }, [startTimeStamp, selectedZone]);

  return { updatedWorkflowTimeAndState };
}
